import React, { useMemo } from 'react';
import {
  FLOWCHART_DOT_GRID_BG,
  FLOWCHART_VISUAL_DOT_PITCH,
} from '../../../SprintDetail/FlowchartView/components/FlowchartCanvas/constants';
import AtlasFolderNode from './AtlasFolderNode';
import AtlasPeek from './AtlasPeek';

const AtlasWorld = ({
  viewW,
  viewH,
  pan,
  zoom,
  cameraRef,
  gridRef,
  nodes,
  edges,
  clusters = [],
  peek,
  draggingIds,
  livePos,
  expandedId,
  selectedIds,
  dropHint = null,
  dropTargetId,
  registerNode,
  registerEdge,
  useDemo,
  onOpenDoc,
  onOpenDocInNewTab,
  onBrowseFolder,
  onFolderPointerDown,
  onDragOverFolder,
  onDragLeaveFolder,
  onDropOnFolder,
  onNodeClick,
  onNodeContextMenu,
  renamingId,
  onRenameCommit,
  onRenameCancel,
  renderNode = null,
  renderPeek = null,
}) => {
  const z = zoom || 1;
  const originX = -pan.x / z;
  const originY = -pan.y / z;

  const worldBox = useMemo(() => {
    let maxX = 400;
    let maxY = 200;
    for (const n of nodes) {
      maxX = Math.max(maxX, n.x + n.width);
      maxY = Math.max(maxY, n.y + n.height);
    }
    for (const c of clusters) {
      maxX = Math.max(maxX, c.x + c.width);
      maxY = Math.max(maxY, c.y + c.height);
    }
    return { w: Math.ceil(maxX + 80), h: Math.ceil(maxY + 80) };
  }, [nodes, clusters]);

  const peekOthers = useMemo(() => {
    const nodeOthers = nodes.filter((n) => n.id !== peek?.folderId);
    const clusterRects = clusters.map((c) => ({
      id: c.id,
      x: c.x,
      y: c.y,
      width: c.width,
      height: c.height,
    }));
    return [...nodeOthers, ...clusterRects];
  }, [nodes, clusters, peek?.folderId]);

  return (
    <>
      <div
        ref={gridRef}
        className="atlas__canvas-grid"
        aria-hidden
        style={{
          backgroundImage: FLOWCHART_DOT_GRID_BG,
          backgroundSize: `${FLOWCHART_VISUAL_DOT_PITCH * z}px ${FLOWCHART_VISUAL_DOT_PITCH * z}px`,
          backgroundPosition: `${pan.x}px ${pan.y}px`,
        }}
      />
      <div
        ref={cameraRef}
        className="atlas__camera"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${z})`,
          '--atlas-zoom': z,
        }}
      >
        <div className="atlas__world">
          {clusters.map((cluster) => (
            <div
              key={cluster.id}
              className="atlas__cluster"
              aria-hidden
              style={{
                width: cluster.width,
                height: cluster.height,
                transform: `translate(${cluster.x}px, ${cluster.y}px)`,
              }}
            />
          ))}

          {edges.length > 0 && (
            <svg
              className="atlas__edges"
              width={worldBox.w}
              height={worldBox.h}
              overflow="visible"
            >
              {edges.map((edge) => (
                <path key={edge.id} ref={registerEdge(edge.id)} className="atlas__edge" />
              ))}
            </svg>
          )}

          {nodes.map((node) => {
            const live = livePos(node.id) || node;
            const nodeProps = {
              node,
              live: { x: live.x, y: live.y },
              expanded: expandedId === node.id,
              selected: selectedIds.has(node.id),
              dragging: draggingIds.has(node.id),
              dropTarget: dropHint?.folderId === node.id && dropHint.zone === 'middle',
              dropInsert:
                dropHint?.folderId === node.id && dropHint.zone !== 'middle'
                  ? dropHint.zone
                  : null,
              registerNode,
              onPointerDown: onFolderPointerDown,
              onDragOver: onDragOverFolder,
              onDragLeave: onDragLeaveFolder,
              onDrop: onDropOnFolder,
              onClick: onNodeClick,
              onContextMenu: onNodeContextMenu,
              renaming: renamingId === node.id,
              onRenameCommit,
              onRenameCancel,
            };
            return renderNode ? (
              <React.Fragment key={node.id}>{renderNode(nodeProps)}</React.Fragment>
            ) : (
              <AtlasFolderNode key={node.id} {...nodeProps} />
            );
          })}

          {peek && draggingIds.size === 0 && !renamingId && (
            renderPeek ? (
              renderPeek({
                peek,
                folder: nodes.find((n) => n.id === peek.folderId) || null,
                others: peekOthers,
                viewport: {
                  x: originX,
                  y: originY,
                  w: viewW / z,
                  h: viewH / z,
                },
                originX: 0,
                originY: 0,
              })
            ) : (
              <AtlasPeek
                peek={peek}
                folder={nodes.find((n) => n.id === peek.folderId) || null}
                others={peekOthers}
                viewport={{
                  x: originX,
                  y: originY,
                  w: viewW / z,
                  h: viewH / z,
                }}
                originX={0}
                originY={0}
                useDemo={useDemo}
                onOpenDoc={onOpenDoc}
                onOpenDocInNewTab={onOpenDocInNewTab}
                onBrowseFolder={onBrowseFolder}
              />
            )
          )}
        </div>
      </div>
    </>
  );
};

export default AtlasWorld;
